import { useTranslations } from '@aikoa/i18n/react'
import { useEffect, useMemo } from 'react'
import { View } from 'react-native'
import { type FeedbackRequestFlowData } from '~/components/feedback/CreateFeedbackRequestFlow'
import { FeedbackScheduleButtons } from '~/components/feedback/FeedbackScheduleButtons'
import { FeedbackSendingMethodButtons } from '~/components/feedback/FeedbackSendingMethodButtons'
import FlowNavigation from '~/components/FlowNavigation'
import { useFlowManager } from '~/components/flows/useFlowManager'
import { SectionContent } from '~/components/layout/Sections'
import { LoadingView } from '~/components/LoadingView'
import { ScrollView } from '~/components/ScrollView'
import { DisplayValue } from '~/components/typography/Content'
import { Heading2 } from '~/components/typography/Titles'
import BottomSheet from '~/components/ui/bottom-sheet'
import { useContactsByIds } from '~/data/contacts'
import {
  getAvailableFeedbackSendingMethods,
  resolveFeedbackSendingMethod,
} from '~/data/feedback/utils'

export function FeedbackSendingSettingsModal({
  disabled,
  selectedContactIds,
  requestData,
  updateData,
  onCancel,
}: {
  disabled?: boolean
  selectedContactIds: string[]
  requestData: Partial<FeedbackRequestFlowData['request-config']>
  updateData: (phaseId: string, data: Partial<FeedbackRequestFlowData['request-config']>) => void
  onCancel?: () => void
}) {
  const { t } = useTranslations('feedback')
  const { canProceed, previousPhase, nextPhase, goToPhase, isLoading } = useFlowManager()
  const { sendTiming, sendingMethod } = requestData

  const { data: contacts = [], isLoading: isLoadingContacts } = useContactsByIds(selectedContactIds)

  const availableSendingMethods = useMemo(
    () => getAvailableFeedbackSendingMethods(contacts),
    [contacts]
  )

  useEffect(() => {
    const resolvedMethod = resolveFeedbackSendingMethod(sendingMethod, availableSendingMethods)

    if (resolvedMethod && resolvedMethod !== sendingMethod) {
      updateData('request-config', {
        sendingMethod: resolvedMethod,
        sendTiming: resolvedMethod === 'sms' ? sendTiming : 'now',
      })
    }
  }, [availableSendingMethods, sendTiming, sendingMethod, updateData])

  const canSendToContact = availableSendingMethods.length > 0

  return (
    <BottomSheet
      modalKey="feedback-sending-settings"
      isOpen
      showHandle={false}
      showCloseButton={false}>
      {isLoadingContacts && contacts.length === 0 && <LoadingView />}
      {(!isLoadingContacts || contacts.length > 0) && (
        <ScrollView
          contentContainerClassName="gap-4 px-6 py-0 pt-6"
          footerClassName="pb-0 bottom-0 absolute"
          className="max-h-[75vh] min-h-[250px] flex-1"
          footer={
            <FlowNavigation
              className="pb-6"
              onNext={() => (!canSendToContact ? goToPhase('select-contact') : nextPhase())}
              nextTestId="feedback-send-submit-button"
              nextDisabled={
                !canProceed ||
                (canSendToContact && !sendingMethod) ||
                (sendingMethod === 'sms' && !sendTiming)
              }
              nextLabel={
                canSendToContact
                  ? !sendingMethod || !sendTiming
                    ? t(($) => $.requestFlow.sendButtonTextNext)
                    : t(($) => $.requestFlow.sendButtonTextSend)
                  : t(($) => $.requestFlow.selectOtherContact)
              }
              isLoading={isLoading}
              onPrevious={() => (onCancel ? onCancel() : previousPhase())}
              previousTestId="feedback-send-cancel-button"
              previousLabel={t(($) => $.requestFlow.cancel)}
              showBackground={false}
            />
          }>
          <Heading2 className="text-center">
            {t(($) => $.requestFlow.sendingSettingsTitle)}
          </Heading2>

          <View className="flex-1 gap-6">
            {availableSendingMethods.length > 1 ? (
              <FeedbackSendingMethodButtons
                // eslint-disable-next-line i18next/no-literal-string
                selectedMethod={sendingMethod ?? 'sms'}
                disabled={disabled}
                onSelectMethod={(method) =>
                  updateData('request-config', {
                    sendingMethod: method,
                    // eslint-disable-next-line i18next/no-literal-string
                    sendTiming: method === 'email' ? 'now' : sendTiming,
                  })
                }
              />
            ) : !canSendToContact ? (
              <SectionContent className="text-center">
                {t(($) => $.requestFlow.noPhoneNumberMessage)}
              </SectionContent>
            ) : null}

            {sendingMethod === 'sms' ? (
              <FeedbackScheduleButtons
                key="send-timing"
                selectedTime={sendTiming ?? null}
                disabled={disabled}
                className="mb-2"
                onSelectTime={(_, sendScheduleKey) => {
                  updateData('request-config', {
                    sendTiming: sendScheduleKey,
                    // eslint-disable-next-line i18next/no-literal-string
                    sendingMethod: 'sms',
                  })
                }}
              />
            ) : (
              <DisplayValue className="text-pretty px-4 text-center">
                {t(($) => $.requestFlow.emailSendingDescription)}
              </DisplayValue>
            )}
          </View>
        </ScrollView>
      )}
    </BottomSheet>
  )
}
